import type {
  ApiJobSynonymFamilyId,
  ApiJobSynonymId,
} from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';

import { Card } from './Card';
import { Container } from './Container';
import { BigButton } from './JobSelector.styled';

type JobSibling = {
  description: string;
  family: { id: string; name: string };
  id: string;
  level: number;
  name: string;
  subFamily: { id: string };
  synonyms: {
    id: string;
    name: string;
  }[];
};

function getSynyonymId(sibling: JobSibling) {
  const synonym = sibling.synonyms.find((s) => s.name === sibling.name);
  return synonym ? synonym.id : null;
}

type Props = {
  onSelect: (arg0: {
    familyId: ApiJobSynonymFamilyId;
    name: string;
    id: string;
    synonymId: ApiJobSynonymId | null;
  }) => void;
  onReset: () => void;
  siblings: JobSibling[];
  setRelatedJobsShowing: (arg0: boolean) => void;
};

export function RelatedJobs({
  setRelatedJobsShowing,
  siblings,
  onSelect,
  onReset,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Container>
      <Box pt={[12, null, 24]} mx={20}>
        <BodySmall>{i18n('Try one of these')}&hellip;</BodySmall>
      </Box>

      {siblings.map((sibling) => (
        <Box
          key={sibling.id}
          pb="15px"
          mt="10px"
          mb="15px"
          borderBottom={`1px solid ${colors.selectionGrey}`}
        >
          <BigButton
            data-qa-id="job-sibling"
            onClick={() => {
              setRelatedJobsShowing(false);
              onSelect({
                name: sibling.name,
                id: sibling.id,
                familyId: sibling.family.id,
                synonymId: getSynyonymId(sibling),
              });
            }}
            key={sibling.id}
          >
            <Card
              content={sibling}
              cta={
                <Button type="button" variant="primary" size="small">
                  {i18n('Choose Title')}
                </Button>
              }
            />
          </BigButton>
        </Box>
      ))}
      <Box mt="10px">
        <Button
          type="button"
          variant="tertiary"
          noPadding
          onClick={() => {
            onReset();
            setRelatedJobsShowing(false);
          }}
        >
          {i18n('These do not match my job')}
        </Button>
      </Box>
    </Container>
  );
}
